/* eslint-disable react-hooks/exhaustive-deps */

import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../config/api.config";
import { useNavigate } from "react-router";
import { useEffect, useState } from "react";
import { connect } from "react-redux";

import { initializeSession, initializeSessionFarms } from "../../redux/action";
import HeaderFarms from "../../utils/header_farms.js";
import API from "../../services/index.js";
import "./styles.css";

function Login(props) {

  const { instance } = useMsal();
  const navigate = useNavigate();

  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [isProcessing, setIsProcessing] = useState(false); // ⛔️ anti-loop flag
  const [loading, setLoading] = useState(true);

  // ============================================================
  // ✅ Verifikasi ke backend
  // ============================================================
  const Verification = async (token) => {
    try {
      const res = await API.getUserCredential(HeaderFarms(token), {
        access_token: token,
      });
      let results;

      try {
        results = await API.getSessionSignin(HeaderFarms(token));
      } catch (sessionError) {
        if (sessionError?.response?.status === 404) {
          navigate("/completed-user", { replace: true });
          return { redirected: true };
        }

        throw sessionError;
      }

      if (res.phone_number && !res.phone_number_activation) {
        return navigate(`/verification/${res.phone_number}/${token}`);
      }

      if (!res.phone_number) {
        return navigate(`/verification/change/${token}`);
      }

      props.setInitializeSession(res);
      props.setInitializeSessionFarms(results);

      localStorage.setItem("session_signin", JSON.stringify(res));
      localStorage.setItem("session_farms", JSON.stringify(results));

      return { redirected: false, data: res };
    } catch (err) {
      console.error("❌ Verification Error:", err);
      throw err;
    }
  };

  // ============================================================
  // ✅ Login flow utama
  // ============================================================
  useEffect(() => {
    let isMounted = true; // ✅ Prevent execution after unmount

    const initLogin = async () => {

      if (isProcessing) return; // ⛔️ cegah loop
      if (!isMounted) return; // ⛔️ cegah double execution
      setIsProcessing(true);

      try {

        const currentURL = window.location.href;
        const url = new URL(currentURL);

        switch (url.hostname) {
          case "hias.citrapalu.net":
          case "hias-staging.citrapalu.net":
          case "hias.gorontalominerals.com":
            return navigate("/hias/create");
          case "operator.citrapalu.net":
            return navigate("/fms/operator/register");
          case "fuel.citrapalu.net":
          case "backdaterf.citrapalu.net":
          case "backdatedf.citrapalu.net":
            return navigate("/fms/menu");
          default:
        }

        if (isOffline) {
          console.warn("⚠ Offline mode tanpa session");
          setLoading(false);
          navigate("/home");
          return;
        }

        await instance.initialize();

        // 🔹 Coba ambil hasil redirect (kalau baru login)
        const response = await instance.handleRedirectPromise().catch(err => {
          console.warn("⚠ handleRedirectPromise error:", err);
          if (err.errorCode === 'interaction_in_progress') {
            console.log("⏳ Interaction already in progress, skipping...");
          }
          return null;
        });

        if (response && response.account) {
          console.log("✅ Login redirect success:", response.account.username);
          instance.setActiveAccount(response.account);

          // ✅ Simpan token & verifikasi user
          localStorage.setItem("offline_access_token", response.accessToken);
          localStorage.setItem(
            "offline_token_expiry",
            response.expiresOn.getTime().toString()
          );

          const verificationResult = await Verification(response.accessToken);

          if (verificationResult?.redirected) {
            return;
          }

          navigate("/home");

          return; // ❗ STOP DI SINI, jangan lanjut ke checkLogin()
        }

        // 🔹 Kalau gak ada hasil redirect, cek akun tersimpan
        const accounts = instance.getAllAccounts();

        if (accounts.length === 0) {
          console.log("🔁 No account → redirect login");
          const inProgress = sessionStorage.getItem('msal.interaction.status');
          if (!inProgress || inProgress === 'interaction_complete') {
            instance.loginRedirect(loginRequest).catch(err => {
              if (err.errorCode !== 'interaction_in_progress') {
                console.error("❌ loginRedirect error:", err);
              }
            });
          }
          return;
        }

        // 🔹 Ada akun → coba ambil token silent
        instance.setActiveAccount(accounts[0]);
        console.log("🔑 Try acquireTokenSilent for:", accounts[0].username);

        try {
          
          const tokenResponse = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          });

          console.log("✅ Token acquired silently");
          localStorage.setItem("offline_access_token", tokenResponse.accessToken);
          localStorage.setItem(
            "offline_token_expiry",
            tokenResponse.expiresOn.getTime().toString()
          );

          const verificationResult = await Verification(tokenResponse.accessToken);

          if (verificationResult?.redirected) {
            return;
          }

          navigate("/home");
        } catch (error) {
          console.warn("⚠ acquireTokenSilent failed:", error.errorCode || error.message);

          // Kalau token expired atau butuh consent
          if (
            error.errorCode === "interaction_required" ||
            error.errorCode === "consent_required"
          ) {
            console.log("🔄 Need user interaction → redirect login");
            // ✅ Check if interaction is already in progress before calling loginRedirect
            const inProgress = sessionStorage.getItem('msal.interaction.status');
            if (!inProgress || inProgress === 'interaction_complete') {
              instance.loginRedirect(loginRequest).catch(err => {
                if (err.errorCode !== 'interaction_in_progress') {
                  console.error("❌ loginRedirect error:", err);
                }
              });
            }
          } else if (error.errorCode !== 'interaction_in_progress') {
            console.warn("⚠ Silent token error (transient):", error.errorCode || error.message);
          }
        }
      } catch (err) {

        console.error("❌ Unexpected error:", err);
      } finally {
        if (isMounted) {
          setLoading(false);
          setIsProcessing(false);
        }
      }
    };

    initLogin();

    // ✅ Cleanup function to prevent double execution
    return () => {
      isMounted = false;
    };
  }, [instance, isOffline, navigate]);

  // ============================================================
  // ✅ Listener Online/Offline
  // ============================================================
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // ============================================================
  // ✅ UI
  // ============================================================
  return (
    <div className="login-container">
      <div className="login-content">
        <div className="login-header">
          <img src="/logo192.png" alt="FARMS Logo" className="login-logo" />
          <h1 className="login-title">FARMS</h1>
          <p className="login-subtitle">Information Technology</p>
        </div>

        <div className="login-body">
          {loading ? (
            <div className="login-message">
              <div className="loading-spinner"></div>
              <p>Authenticating...</p>
            </div>
          ) : isOffline ? (
            <div className="offline-message">
              <div className="offline-icon">📱</div>
              <h3>Offline Mode</h3>
              <p>You are currently offline. Using cached data for access.</p>
              <div className="loading-spinner"></div>
            </div>
          ) : (
            <div className="login-message">
              <div className="loading-spinner"></div>
              <p>Redirecting...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Redux setup
const reduxState = (state) => ({
  verification: state.verification,
  session: state.session,
});

const reduxDispatch = (dispatch) => ({
  setInitializeSession: (data) => dispatch(initializeSession(data)),
  setInitializeSessionFarms: (data) => dispatch(initializeSessionFarms(data)),
});

export default connect(reduxState, reduxDispatch)(Login);
